import { View, Modal, Pressable, ScrollView, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { SubHeading, Body, Caption, ThemedText } from '@/components/ui/Text';
import type { Notification } from '../types';
import { sortNotifications } from '../adapters';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

type NotificationModalProps = {
  visible: boolean;
  onClose: () => void;
  notifications: Notification[];
  onAction?: (notification: Notification) => void;
  onDismiss?: (notification: Notification) => void;
  onDismissAll?: () => void;
  title?: string;
};

const severityColor = (
  severity: Notification['severity'],
  themeColors: ReturnType<typeof useTheme>['colors'],
) => {
  switch (severity) {
    case 'critical':
      return themeColors.error || '#FF3B30';
    case 'warning':
      return themeColors.warning || '#FF9500';
    default:
      return themeColors.primary;
  }
};

const kindIconMap: Record<Notification['kind'], keyof typeof Ionicons.glyphMap> = {
  visa: 'document-text-outline',
  offline: 'download-outline',
  collaborators: 'people-outline',
  docs: 'folder-outline',
  check_in: 'time-outline',
  budget: 'wallet-outline',
  missing_transport: 'airplane-outline',
  missing_lodging: 'bed-outline',
  weather: 'cloud-outline',
  document: 'document-outline',
  safety: 'shield-checkmark-outline',
  other: 'information-circle-outline',
};

export function NotificationModal({
  visible,
  onClose,
  notifications,
  onAction,
  onDismiss,
  onDismissAll,
  title = 'Notifications',
}: NotificationModalProps) {
  const theme = useTheme();
  const insets = useSafeAreaInsets();

  const items = sortNotifications(notifications);

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onClose}
    >
      <View
        style={{
          flex: 1,
          backgroundColor: theme.colors.background,
          paddingTop: Platform.OS === 'android' ? insets.top + theme.spacing(1.5) : theme.spacing(2),
        }}
      >
        {/* Header */}
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingHorizontal: theme.spacing(2),
            paddingBottom: theme.spacing(2),
            borderBottomWidth: 1,
            borderBottomColor: theme.colors.borderMuted,
          }}
        >
          <SubHeading style={{ fontSize: 20 }}>{title}</SubHeading>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
            {items.length > 0 && onDismissAll && (
              <Pressable
                onPress={onDismissAll}
                style={({ pressed }) => ({
                  opacity: pressed ? 0.7 : 1,
                  padding: theme.spacing(0.5),
                })}
              >
                <ThemedText
                  variant="caption"
                  style={{
                    color: theme.colors.primary,
                    fontSize: 14,
                    fontWeight: '600',
                  }}
                >
                  Clear all
                </ThemedText>
              </Pressable>
            )}
            <Pressable
              onPress={onClose}
              accessibilityLabel="Close notifications"
              style={({ pressed }) => ({
                opacity: pressed ? 0.7 : 1,
                padding: theme.spacing(0.5),
              })}
            >
              <Ionicons name="close" size={24} color={theme.colors.text} />
            </Pressable>
          </View>
        </View>

        {/* Notifications List */}
        <ScrollView
          contentContainerStyle={{
            padding: theme.spacing(2),
            gap: theme.spacing(2),
            paddingBottom: insets.bottom + theme.spacing(2),
          }}
        >
          {items.length === 0 ? (
            <Card variant="outlined">
              <View
                style={{
                  alignItems: 'center',
                  paddingVertical: theme.spacing(4),
                  gap: theme.spacing(1),
                }}
              >
                <Ionicons
                  name="checkmark-circle-outline"
                  size={48}
                  color={theme.colors.textSecondary}
                />
                <ThemedText variant="bodyStrong">No alerts</ThemedText>
                <Body color={theme.colors.textSecondary} style={{ textAlign: 'center' }}>
                  All caught up. We’ll surface new alerts here.
                </Body>
              </View>
            </Card>
          ) : (
            items.map((notification) => {
              const icon = kindIconMap[notification.kind] ?? 'information-circle-outline';
              const color = severityColor(notification.severity, theme.colors);
              const isCritical = notification.severity === 'critical';
              const tripName = typeof notification.meta?.tripName === 'string' ? notification.meta.tripName : null;

              return (
                <Card key={notification.id} variant="outlined">
                  <View style={{ gap: theme.spacing(1.5) }}>
                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'flex-start',
                        gap: theme.spacing(1.5),
                      }}
                    >
                      <View
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 20,
                          backgroundColor: color + '20',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        <Ionicons
                          name={icon as any}
                          size={20}
                          color={color}
                        />
                      </View>
                      <View style={{ flex: 1, gap: theme.spacing(0.5) }}>
                        <View
                          style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <ThemedText
                            variant="bodyStrong"
                            style={{
                              fontSize: 16,
                              flex: 1,
                              color: theme.colors.text,
                            }}
                          >
                            {notification.title}
                          </ThemedText>
                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                            {isCritical && (
                              <View
                                style={{
                                  paddingHorizontal: theme.spacing(0.75),
                                  paddingVertical: 2,
                                  borderRadius: theme.radii.sm,
                                  backgroundColor: color,
                                }}
                              >
                                <Caption
                                  style={{
                                    color: theme.colors.surface,
                                    fontSize: 10,
                                    fontWeight: '700',
                                  }}
                                >
                                  URGENT
                                </Caption>
                              </View>
                            )}
                            {onDismiss && (
                              <Pressable
                                onPress={() => onDismiss(notification)}
                                accessibilityLabel="Dismiss notification"
                                style={({ pressed }) => ({
                                  opacity: pressed ? 0.7 : 1,
                                  padding: theme.spacing(0.5),
                                })}
                              >
                                <Ionicons
                                  name="close-circle-outline"
                                  size={20}
                                  color={theme.colors.textSecondary}
                                />
                              </Pressable>
                            )}
                          </View>
                        </View>
                        {tripName && (
                          <Caption color={theme.colors.textSecondary} style={{ fontSize: 12 }}>
                            {tripName}
                          </Caption>
                        )}
                        <Body
                          color={theme.colors.textSecondary}
                          style={{
                            marginTop: theme.spacing(0.5),
                          }}
                        >
                          {notification.body}
                        </Body>
                      </View>
                    </View>
                    {notification.cta?.label && (
                      <Button
                        label={notification.cta.label}
                        variant="secondary"
                        onPress={() => onAction?.(notification)}
                        style={{ marginTop: theme.spacing(0.5) }}
                      />
                    )}
                  </View>
                </Card>
              );
            })
          )}
        </ScrollView>
      </View>
    </Modal>
  );
}
